// Plan settings — subscription tier placeholder (sellers only).
//
// Volkan direction 2026-05-23 (Settings 10-section expansion item #3):
// Shopify-pattern tier card layout. Mock UI · backend persistence M3 Stage 4
// SPEC.md Faz 4 (Wallets + Settlement extension — billing alignment).

import { Check } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { useT } from '../../i18n'
import { planI18n } from '../../i18n/settings/plan'
import { useToastStore } from '../../lib/toast'
import type { SettingsSurface } from './Settings'

type TierId = 'free' | 'starter' | 'pro'

type PlanTier = {
  id: TierId
  price: string
  highlighted?: boolean
}

const TIERS: PlanTier[] = [
  { id: 'free', price: '$0' },
  { id: 'starter', price: '$29', highlighted: true },
  { id: 'pro', price: '$79' },
]

export function Plan({ surface }: { surface: SettingsSurface }) {
  void surface // surface filter applied at Settings shell level (sellers only)
  const push = useToastStore((s) => s.push)
  const t = useT(planI18n)
  const current: TierId = 'starter'

  const notifyMock = (tierId: TierId) => {
    const label = t.tiers[tierId].label
    push({
      variant: 'info',
      title: `${label} · mock-only`,
      description: t.toastTail,
      duration_ms: 4000,
    })
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-slate-900">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-slate-600">{t.pageDescription}</p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {TIERS.map((tier) => {
          const isCurrent = tier.id === current
          const tierStrings = t.tiers[tier.id]
          return (
            <div
              key={tier.id}
              className={
                'flex flex-col rounded-lg border bg-white p-5 ' +
                (tier.highlighted
                  ? 'border-brand-300 ring-1 ring-brand-200'
                  : 'border-slate-200')
              }
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {tierStrings.label}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {tierStrings.description}
                  </p>
                </div>
                {isCurrent && (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800">
                    {t.currentBadge}
                  </span>
                )}
              </div>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-2xl font-semibold text-slate-900">
                  {tier.price}
                </span>
                <span className="text-xs text-slate-500">{t.period}</span>
              </div>

              <ul className="mt-4 space-y-2 text-sm text-slate-700">
                {tierStrings.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5">
                <Button
                  variant={isCurrent ? 'outline' : 'primary'}
                  size="sm"
                  disabled={isCurrent}
                  onClick={() => notifyMock(tier.id)}
                  className="w-full"
                >
                  {isCurrent ? t.currentPlan : t.switchTo(tierStrings.label)}
                </Button>
              </div>
            </div>
          )
        })}
      </div>

      <p className="text-xs text-slate-400">{t.footerNote}</p>
    </div>
  )
}
